import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** A school of little fish swimming across together (Dock Town's Ocean schools). */
interface School {
  x: number;
  y: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  phase: number;
  rank: number;
  shown: number;
  fish: { t: number; across: number; size: number }[];
}

/** A mote drifting in the water: marine snow, or a glowing speck of plankton. */
interface Mote {
  x: number;
  y: number;
  r: number;
  drift: number;
  sink: number;
  phase: number;
  rate: number;
  glow: number;
  rank: number;
}

/** One of the great shapes passing far off in the blue: a whale or a shark (owner). */
interface Giant {
  kind: 'whale' | 'shark' | 'angler';
  x: number;
  y: number;
  dir: 1 | -1;
  speed: number;
  len: number;
  phase: number;
  wait: number;
  // the anglerfish's lure: which of the circle's colours (second to fifth) it glows this pass
  tint?: number;
}

/** A bubble rising from the seabed. */
interface Bubble {
  x: number;
  y: number;
  r: number;
  speed: number;
  wobble: number;
}

/**
 * Ocean's hero board (2026-10-16): under the sea, looking up toward the light, dark and calm like the
 * theme. The water deepens from a teal glow under the rippling surface to near black below; soft rays
 * of light slant down from the surface and sway; the seabed lies in shadow at the bottom, rocks and
 * corals with a hint of the palette's coral and sand, and seagrass swaying; marine snow drifts down,
 * specks of plankton glow cyan and green, a few bubbles rise; and schools of little dark fish swim by
 * (Dock Town's Ocean). A gust (the pointer, a finger, the counter counting) brings more schools out,
 * swimming faster (only the fish: owner). Laid out by the stage's height with fixed seeds.
 */
@Component( {
  selector: 'app-hero-ocean',
  template: '<canvas #canvas class="ho-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.ho-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroOceanComponent extends HeroMotifBase {

  /**
   * A humpback's depth along its length, measured from "Balaenoptera novaeangliae" by T. Michael
   * Keesey (PhyloPic, public domain, image fd8d3e5e-24a0-4aa2-9211-987ff86007dd): x from the snout,
   * then the top and the bottom of the body there, in that drawing's pixels (fins left out).
   */

  private static readonly HUMP_PROFILE: [ number, number, number ][] = [
    [ 0, 104, 168 ], [ 20, 72, 196 ], [ 60, 42, 222 ], [ 100, 33, 246 ], [ 200, 18, 303 ], [ 300, 12, 336 ],
    [ 400, 14, 355 ], [ 500, 22, 365 ], [ 600, 19, 364 ], [ 700, 22, 360 ], [ 800, 26, 350 ], [ 900, 30, 320 ],
    [ 1000, 40, 274 ], [ 1100, 52, 236 ], [ 1200, 71, 214 ], [ 1250, 84, 201 ], [ 1300, 97, 193 ], [ 1360, 112, 182 ],
  ];

  private back: HTMLCanvasElement | null = null;
  private bed: HTMLCanvasElement | null = null;
  private rayCanvas: HTMLCanvasElement | null = null;
  private giants: Giant[] = [];
  private schools: School[] = [];
  private motes: Mote[] = [];
  private bubbles: Bubble[] = [];
  private bubbleDue = 0;
  private scaleK = 1;
  private grass: { x: number; h: number; w: number; phase: number; rate: number; tone: number; base: number; front: boolean; clump?: number }[] = [];
  // where each front clump stands, for the sand heaped over its roots
  private clumps: { x: number; y: number; w: number }[] = [];

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.bed = null;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.back = null;
    this.bed = null;
    const rand = this.seededLocal( 13 );
    // the schools: a pool, each out only while the gust is strong enough for its rank
    this.schools = [];
    // up to 40 out at full charge (owner)
    for ( let i = 0; i < 40; i++ ) {
      const fish: School[ 'fish' ] = [];
      for ( let f = 0; f < 24; f++ ) { fish.push( { t: rand(), across: rand() * 2 - 1, size: rand() } ); }
      const far = rand();
      this.schools.push( {
        x: rand() * W, y: H * ( 0.06 + 0.76 * rand() ), dir: rand() < 0.5 ? 1 : -1,
        speed: ( 26 + rand() * 30 ) * k * ( 1 - 0.4 * far ), size: ( 38 - 16 * far ) * k,
        phase: rand() * Math.PI * 2, rank: i === 0 ? 0 : rand(), shown: 0, fish,
      } );
    }
    // the motes: marine snow everywhere, and the plankton that glows
    this.motes = [];
    for ( let i = 0; i < 220; i++ ) {
      const glow = rand() < 0.35 ? 1 : 0;
      this.motes.push( {
        x: rand() * W, y: rand() * H, r: ( glow ? 1.2 + rand() * 1.8 : 0.6 + rand() * 1.2 ) * k,
        drift: ( rand() - 0.5 ) * 6 * k, sink: ( 2 + rand() * 6 ) * k, phase: rand() * Math.PI * 2,
        rate: 1 + Math.floor( rand() * 4 ), glow, rank: rand(),
      } );
    }
    // the seagrass along the bed
    this.grass = [];
    const UW = Math.min( W, H * 2.1 );
    const n = Math.round( 70 * Math.max( W, UW ) / UW );
    for ( let i = 0; i < n; i++ ) {
      // rooted below the bed's own line, some deeper than others, so no base line shows (owner)
      const gx = rand() * W;
      this.grass.push( {
        x: gx, h: ( 30 + rand() * 70 ) * k, w: ( 2 + rand() * 3 ) * k,
        phase: rand() * Math.PI * 2, rate: 1 + Math.floor( rand() * 3 ), tone: rand(),
        base: this.floorY( gx ) + ( 3 + rand() * 12 ) * k, front: false,
      } );
    }
    // and nearer clumps in front of the rocks too (owner: not only in the middle), taller and darker
    const clumps = Math.round( 9 * Math.max( W, UW ) / UW );
    this.clumps = [];
    for ( let c = 0; c < clumps; c++ ) {
      const cx = rand() * W;
      const cy = H * ( 0.965 + rand() * 0.04 );
      const blades = 5 + Math.floor( rand() * 7 );
      this.clumps.push( { x: cx, y: cy, w: ( 34 + rand() * 20 ) * k } );
      for ( let i = 0; i < blades; i++ ) {
        this.grass.push( {
          x: cx + ( rand() - 0.5 ) * 40 * k, h: ( 50 + rand() * 90 ) * k, w: ( 3 + rand() * 4 ) * k,
          phase: rand() * Math.PI * 2, rate: 1 + Math.floor( rand() * 3 ), tone: rand() * 0.5,
          base: cy + ( rand() - 0.2 ) * 8 * k, front: true, clump: this.clumps.length - 1,
        } );
      }
    }
    this.bubbles = [];
    // the great shapes far off: a whale and two sharks, taking turns to pass, never all at once
    this.giants = [
      // higher up and farther off, so they look smaller (owner)
      { kind: 'whale', x: 0, y: H * 0.42, dir: 1, speed: 12 * k, len: 320 * k, phase: 0, wait: 0 },
      { kind: 'shark', x: 0, y: H * 0.32, dir: -1, speed: 22 * k, len: 110 * k, phase: 1, wait: 5 },
      { kind: 'shark', x: 0, y: H * 0.48, dir: 1, speed: 19 * k, len: 90 * k, phase: 2, wait: 12 },
      // and down by the bed, an anglerfish with its lure alight (owner)
      { kind: 'angler', x: 0, y: H * 0.74, dir: -1, speed: 6.5 * k, len: this.anglerLen( H * 0.74 ), phase: 0, wait: 3, tint: Math.floor( Math.random() * 4 ) },
      // more than one of them (owner), each with its own light and its own time
      { kind: 'angler', x: 0, y: H * 0.7, dir: 1, speed: 6 * k, len: this.anglerLen( H * 0.7 ), phase: 1.5, wait: 9, tint: Math.floor( Math.random() * 4 ) },
      { kind: 'angler', x: 0, y: H * 0.78, dir: -1, speed: 7 * k, len: this.anglerLen( H * 0.78 ), phase: 3, wait: 16, tint: Math.floor( Math.random() * 4 ) },
    ];
    for ( const g of this.giants ) { g.x = g.dir > 0 ? -g.len : W + g.len; }
    // the anglerfish each start with a different light, at random (owner)
    const lights = [ 0, 1, 2, 3 ].sort( () => Math.random() - 0.5 );
    this.giants.filter( g => g.kind === 'angler' ).forEach( ( g, i ) => { g.tint = lights[ i ]; g.speed = ( 4.5 + Math.random() * 4.5 ) * k; } );
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const scale = ctx.getTransform().a;
    if ( this.frozen ) {
      // the page's wallpaper: the schools of fish alone, dark as on the board, all down the page (owner)
      this.drawWallSchools( ctx );
      return;
    }
    if ( !this.back ) { this.bake( scale ); }
    if ( !this.back || !this.bed ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawGiants( ctx, dt );
    this.drawRays( ctx );
    this.drawMotes( ctx, dt, false );
    this.drawSchools( ctx, dt );
    this.drawBubbles( ctx, dt );
    this.drawGrass( ctx, false );
    ctx.drawImage( this.bed, 0, 0, W, H );
    // the clumps in front one by one, farthest first, each with its own soil over its roots, so no
    // clump's soil ever lies over a nearer clump's blades (owner)
    this.clumps.map( ( c, i ) => i ).sort( ( a, b ) => this.clumps[ a ].y - this.clumps[ b ].y )
      .forEach( i => this.drawGrass( ctx, true, i ) );
    this.drawMotes( ctx, dt, true );
  }

  /**
   * The giants far off in the blue (owner): a blue whale and sharks gliding slowly across, in the
   * sea's own colour a little darkened, as if steeped in the water at their depth. They move as the
   * real ones do: the whale's flukes beat up and down, the wave running back along its body, its fins
   * rowing slowly; the shark's tail sweeps side to side (seen from the side, the fin turns edge-on
   * and back), its body swaying a little and its head barely. Each waits a while before passing again.
   */
  private drawGiants( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    for ( const g of this.giants ) {
      if ( dt > 0 ) {
        if ( g.wait > 0 ) {
          g.wait -= dt;
          if ( g.wait <= 0 ) { g.x = g.dir > 0 ? -g.len : W + g.len; }
        } else {
          // a little quicker while the counter counts (owner)
          g.x += g.dir * g.speed * ( 1 + 0.5 * this.energy ) * dt;
          g.phase += dt * ( g.kind === 'whale' ? 0.55 : g.kind === 'angler' ? 1.2 : 1.9 );
          if ( g.x > W + g.len || g.x < -g.len ) {
            g.wait = 5 + Math.random() * 9;
            g.dir = Math.random() < 0.5 ? 1 : -1;
            g.y = g.kind === 'angler' ? H * ( 0.69 + Math.random() * 0.1 ) : H * ( 0.3 + Math.random() * 0.2 );
            if ( g.kind === 'angler' ) {
              g.len = this.anglerLen( g.y );
              // each at its own pace, a little apart from the others (owner)
              g.speed = ( 4.5 + Math.random() * 4.5 ) * this.scaleK;
            }
            // a new pass, a new light for the lure, never the colour another anglerfish is showing (owner)
            const taken = this.giants.filter( o => o !== g && o.kind === 'angler' ).map( o => o.tint );
            const free = [ 0, 1, 2, 3 ].filter( c => !taken.includes( c ) );
            g.tint = free[ Math.floor( Math.random() * free.length ) ];
          }
        }
      }
      if ( g.wait > 0 ) { continue; }
      const y = g.y + Math.sin( g.phase * 0.35 ) * g.len * 0.03;
      const water = this.waterAt( y );
      const back = this.mix( water, [ 2, 6, 8 ], 0.42 );
      const belly = this.mix( water, [ 2, 6, 8 ], 0.22 );
      ctx.save();
      ctx.translate( g.x, y );
      ctx.scale( g.dir, 1 );
      if ( g.kind === 'whale' ) { this.whale( ctx, g.len, g.phase, back ); } else if ( g.kind === 'angler' ) { /* farther back: smaller, and steeped more in the water (owner) */ this.angler( ctx, g.len, g.phase, this.mix( water, [ 2, 6, 8 ], 0.15 ), g.tint || 0 ); } else { this.shark( ctx, g.len, g.phase, back, belly, water ); }
      ctx.restore();
    }
  }

  /** An anglerfish's size by its depth (owner): the deepest the biggest, the highest the smallest. */
  private anglerLen( y: number ): number {
    const t = Math.max( 0, Math.min( 1, ( y / this.height - 0.69 ) / 0.1 ) );
    return ( 22 + 26 * t ) * this.scaleK;
  }

  /** The water's colour at height y, as the board's gradient paints it. */
  private waterAt( y: number ): RGB {
    const [ teal, green ] = this.five();
    const night: RGB = [ 4, 10, 12 ];
    const sea = this.mix( this.mix( teal, green, 0.16 ), night, 0.18 );
    const t = Math.max( 0, Math.min( 1, y / this.height ) );
    return t < 0.35 ? this.mix( this.mix( sea, [ 63, 180, 216 ], 0.25 ), this.mix( sea, night, 0.25 ), t / 0.35 )
      : t < 0.75 ? this.mix( this.mix( sea, night, 0.25 ), this.mix( sea, night, 0.6 ), ( t - 0.35 ) / 0.4 )
        : this.mix( this.mix( sea, night, 0.6 ), this.mix( sea, night, 0.82 ), ( t - 0.75 ) / 0.25 );
  }

  /**
   * A body along a bending spine, facing right, `L` long: `bend(u)` lifts the spine at u (0 the snout,
   * 1 the tail's root), `top(u)` and `bottom(u)` its depth above and below. Returns the outline's
   * points and a way to find the spine.
   */
  private spineBody( L: number, bend: ( u: number ) => number, top: ( u: number ) => number, bottom: ( u: number ) => number, noseLen = 0.018, noseDrop = 0 ) {
    const n = 72;
    const at = ( u: number ) => ( { x: ( 0.5 - u ) * L, y: bend( u ) } );
    const upper: [ number, number ][] = [];
    const lower: [ number, number ][] = [];
    for ( let i = 0; i <= n; i++ ) {
      const u = i / n;
      const p = at( u );
      upper.push( [ p.x, p.y - top( u ) ] );
      lower.push( [ p.x, p.y + bottom( u ) ] );
    }
    // a rounded snout ahead of the first section, never a flat front (owner: it read as a hammerhead)
    const p0 = at( 0 );
    // noseDrop lowers the tip toward the chin, for a lower jaw that reaches past the upper
    const nose: [ number, number ] = [ p0.x + L * noseLen, p0.y + ( bottom( 0 ) - top( 0 ) ) * 0.5 + bottom( 0 ) * noseDrop ];
    return { at, outline: [ nose, ...upper, ...lower.reverse() ] };
  }

  private smoothPath( f: CanvasRenderingContext2D, pts: [ number, number ][] ): void {
    f.beginPath();
    f.moveTo( pts[ 0 ][ 0 ], pts[ 0 ][ 1 ] );
    for ( let i = 1; i < pts.length - 1; i++ ) {
      f.quadraticCurveTo( pts[ i ][ 0 ], pts[ i ][ 1 ], ( pts[ i ][ 0 ] + pts[ i + 1 ][ 0 ] ) / 2, ( pts[ i ][ 1 ] + pts[ i + 1 ][ 1 ] ) / 2 );
    }
    f.lineTo( pts[ pts.length - 1 ][ 0 ], pts[ pts.length - 1 ][ 1 ] );
    f.closePath();
  }

  /**
   * A humpback whale, drawn part by part from its anatomy (owner), as a dark silhouette in one tone
   * like the other giants. Its body's depth along its length is measured from T. Michael Keesey's
   * public-domain humpback on PhyloPic (`HUMP_PROFILE`, in that drawing's pixels, the head at x 0 and
   * the tail's root at 1360): the flat rostrum, the faint mouth line arching
   * back, the deep throat with its pleats back to the navel, the small dorsal fin on
   * its hump, the slim tail stock and the broad flukes with a serrated trailing edge; and its two very
   * long flippers, the far one showing below the belly behind the near one.
   * It swims as they do: the flukes stroke up and down, the bend growing toward the tail and running
   * back along it, the body rising a little on each stroke and the head dipping against it; the
   * flippers sweep slowly to steer, the far one a little behind the near one.
   */
  private whale( f: CanvasRenderingContext2D, L: number, phase: number, back: RGB ): void {
    // the tail stock drawn a little longer than the measure (owner): everything behind x 900 stretched
    const stretch = 1.3;
    const P = HeroOceanComponent.HUMP_PROFILE.map( ( [ x, t, b ] ) => [ x > 900 ? 900 + ( x - 900 ) * stretch : x, t, b ] );
    const span = P[ P.length - 1 ][ 0 ];
    const px = ( v: number ) => v / span * L;
    // a smooth curve through the measured points (Catmull-Rom), so the outline flows without bumps (owner)
    const row = ( u: number ) => {
      const x = u * span;
      let i = 1;
      while ( i < P.length - 1 && x > P[ i ][ 0 ] ) { i++; }
      const p0 = P[ Math.max( 0, i - 2 ) ], p1 = P[ i - 1 ], p2 = P[ i ], p3 = P[ Math.min( P.length - 1, i + 1 ) ];
      const t = Math.max( 0, Math.min( 1, ( x - p1[ 0 ] ) / ( p2[ 0 ] - p1[ 0 ] || 1 ) ) );
      const cr = ( a: number, b: number, c: number, d: number ) =>
        0.5 * ( 2 * b + ( -a + c ) * t + ( 2 * a - 5 * b + 4 * c - d ) * t * t + ( -a + 3 * b - 3 * c + d ) * t * t * t );
      return [ cr( p0[ 1 ], p1[ 1 ], p2[ 1 ], p3[ 1 ] ), cr( p0[ 2 ], p1[ 2 ], p2[ 2 ], p3[ 2 ] ) ];
    };
    const stroke = phase * 2;
    // the bend: next to nothing in the front half, growing fast toward the tail, the wave running back
    const wave = ( u: number ) => 0.055 * L * Math.pow( Math.max( 0, ( u - 0.35 ) / 0.65 ), 2.2 ) * Math.sin( stroke - u * 3 );
    const bend = ( u: number ) => {
      const [ t, b ] = row( u );
      return px( ( t + b ) / 2 - 190 ) + wave( u );
    };
    const top = ( u: number ) => { const [ t, b ] = row( u ); return px( ( b - t ) / 2 ); };
    const bottom = top;
    // the body rises a little on each stroke and the head dips against the tail
    const heave = Math.sin( stroke + 1.2 ) * 0.008 * L;
    const pitch = -Math.cos( stroke ) * 0.012 + Math.sin( phase * 0.4 ) * 0.03;
    f.save();
    f.translate( 0, heave );
    f.rotate( pitch );
    // a broad, blunt head, never a point (owner)
    // the upper jaw no longer than the lower: the two meet in one rounded front (owner)
    const body = this.spineBody( L, bend, top, bottom, 0.01, 0.2 );
    const tone = f.createLinearGradient( 0, -0.15 * L, 0, 0.15 * L );
    tone.addColorStop( 0, this.rgba( this.mix( back, [ 0, 0, 0 ], 0.15 ), 1 ) );
    tone.addColorStop( 1, this.rgba( back, 1 ) );
    const far = this.mix( back, [ 0, 0, 0 ], 0.25 );
    const line = this.mix( back, [ 0, 0, 0 ], 0.38 );
    const flipper = ( u: number, angle: number, len: number, fill: RGB | CanvasGradient ) => {
      const root = body.at( u );
      f.save();
      f.translate( root.x, root.y + bottom( u ) * 0.55 );
      f.rotate( angle );
      f.scale( 1, -1 );
      f.fillStyle = Array.isArray( fill ) ? this.rgba( fill, 1 ) : fill;
      f.beginPath();
      f.moveTo( 0.015 * L, -0.02 * L );
      // the leading edge with its knobs
      for ( let i = 1; i <= 11; i++ ) {
        const t = i / 11;
        const x = -len * t;
        const y = -0.02 * L + 0.02 * L * t - Math.sin( t * Math.PI ) * 0.012 * L;
        const bump = ( i % 2 ) * 0.006 * L * ( 1 - t * 0.5 );
        f.quadraticCurveTo( x + len * 0.045, y - bump, x, y );
      }
      f.quadraticCurveTo( -len * 1.03, 0.008 * L, -len * 0.95, 0.014 * L );
      f.quadraticCurveTo( -len * 0.5, 0.042 * L, 0.015 * L, 0.024 * L );
      f.closePath();
      f.fill();
      f.restore();
    };
    // steering: slow sweeps, the far flipper a little behind the near one
    const sweep = Math.sin( phase * 0.8 );
    const sweepFar = Math.sin( phase * 0.8 - 0.7 );
    // a negative turn lays them down and back below the body (a positive one stood them up over it)
    flipper( 0.27, -( 0.72 + sweepFar * 0.26 ), 0.31 * L, far );
    // the flukes behind the root: flat, so from the side they show as a blade that thickens as it tips
    const root = body.at( 1 );
    const pre = body.at( 0.96 );
    const tilt = Math.atan2( root.y - pre.y, root.x - pre.x );
    const tip = Math.cos( stroke - 3 );
    f.save();
    f.translate( root.x, root.y );
    f.rotate( tilt );
    const half = 0.012 * L + 0.05 * L * Math.abs( tip );
    f.fillStyle = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.1 ), 1 );
    f.beginPath();
    f.moveTo( -0.02 * L, -0.03 * L );
    f.quadraticCurveTo( 0.05 * L, -half * 0.9, 0.12 * L, -half );
    for ( let i = 1; i <= 6; i++ ) {
      const t = i / 6;
      f.lineTo( 0.12 * L - t * 0.022 * L + ( i % 2 ) * 0.006 * L, -half * ( 1 - t ) );
    }
    for ( let i = 1; i <= 6; i++ ) {
      const t = i / 6;
      f.lineTo( 0.098 * L + t * 0.022 * L + ( i % 2 ) * 0.006 * L, half * t );
    }
    f.quadraticCurveTo( 0.05 * L, half * 0.9, -0.02 * L, 0.03 * L );
    f.closePath();
    f.fill();
    f.restore();
    // the body
    f.fillStyle = tone;
    this.smoothPath( f, body.outline );
    f.fill();
    // the small dorsal fin on its hump
    const d = body.at( 0.72 );
    const dTop = d.y - top( 0.72 );
    f.beginPath();
    f.moveTo( d.x + 0.05 * L, dTop + 0.012 * L );
    f.quadraticCurveTo( d.x, dTop - 0.012 * L, d.x - 0.03 * L, dTop - 0.035 * L );
    f.quadraticCurveTo( d.x - 0.03 * L, dTop - 0.005 * L, d.x - 0.06 * L, dTop + 0.012 * L );
    f.closePath();
    f.fill();
    // the details, a shade darker than the body, as in a silhouette seen through water
    f.save();
    this.smoothPath( f, body.outline );
    f.clip();
    // the throat pleats, from the chin back to the navel, a little lighter than the body so they show (owner)
    f.strokeStyle = this.rgba( this.mix( back, [ 200, 215, 215 ], 0.14 ), 0.55 );
    f.lineWidth = Math.max( 0.6, L * 0.0028 );
    for ( let i = 0; i < 10; i++ ) {
      const depth = 0.08 + i * 0.07;
      f.beginPath();
      let first = true;
      // from the very tip of the jaw, so they run right up to the mouth (owner)
      for ( let u = 0; u <= 0.52 - i * 0.012; u += 0.015 ) {
        const p = body.at( u );
        const y = p.y + bottom( u ) * ( 1 - depth );
        if ( first ) { f.moveTo( p.x, y ); first = false; } else { f.lineTo( p.x, y ); }
      }
      f.stroke();
    }
    f.restore();
    // the mouth line, arching from the tip of the snout back, faint (owner)
    f.strokeStyle = this.rgba( line, 0.35 );
    f.lineWidth = Math.max( 0.8, L * 0.004 );
    f.beginPath();
    f.moveTo( body.outline[ 0 ][ 0 ] - 0.004 * L, body.outline[ 0 ][ 1 ] - 0.004 * L );
    for ( let u = 0.0; u <= 0.23; u += 0.01 ) {
      const p = body.at( u );
      const arch = Math.sin( Math.min( 1, u / 0.2 ) * Math.PI * 0.8 ) * 0.25;
      const y = p.y + bottom( u ) * ( 0.05 - arch * 0.6 ) + ( u > 0.18 ? ( u - 0.18 ) * 0.6 * L : 0 );
      f.lineTo( p.x, y );
    }
    f.stroke();
    // the near flipper, in front of everything
    flipper( 0.27, -( 0.62 + sweep * 0.3 ), 0.33 * L, this.mix( back, [ 0, 0, 0 ], 0.04 ) );
    f.restore();
  }

  /**
   * The anglerfish, drawn for looks (owner): clean smooth curves and nothing ragged. A round head
   * flowing in one S into a slim body and a fanned tail, a soft sail of a dorsal fin, a crescent mouth
   * a little open (cut into the outline, so the water shows) with a neat row of slender curved fangs,
   * a small leaf of a side fin, and the rod rising from the brow in a tapering arc to the glowing orb
   * of the lure before the mouth; all one dark tone like the other giants, the lure's glow catching
   * the brow a little. The tail sways, the fin rows, the lure bobs and breathes.
   */
  private angler( f: CanvasRenderingContext2D, L: number, phase: number, back: RGB, tint: number ): void {
    // the lure's light: one of the circle's second to fifth colours, picked at random for each pass (owner)
    const lure = this.five()[ 1 + ( tint % 4 ) ];
    const e = this.energy;
    // while the counter counts it stops blinking and stays at its brightest (owner)
    const pulse = ( ( 0.75 + 0.25 * Math.sin( phase * 3 ) ) * ( 1 - e ) + e ) * ( 1 + 0.6 * e );
    const glowC = this.mix( lure, [ 255, 255, 255 ], 0.25 );
    const X = ( v: number ) => v * L;
    const sway = Math.sin( phase * 2 );
    const fin = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.06 ), 0.92 );
    // the tail: a fan, swaying about its root
    f.save();
    f.translate( X( -0.37 ), 0 );
    f.rotate( sway * 0.12 );
    f.fillStyle = fin;
    f.beginPath();
    f.moveTo( 0, X( -0.04 ) );
    f.quadraticCurveTo( X( -0.08 ), X( -0.15 ), X( -0.18 ), X( -0.14 ) );
    f.quadraticCurveTo( X( -0.13 ), X( -0.07 ), X( -0.2 ), 0 );
    f.quadraticCurveTo( X( -0.13 ), X( 0.07 ), X( -0.18 ), X( 0.14 ) );
    f.quadraticCurveTo( X( -0.08 ), X( 0.15 ), 0, X( 0.04 ) );
    f.closePath();
    f.fill();
    f.strokeStyle = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.35 ), 0.5 );
    f.lineWidth = Math.max( 0.4, X( 0.003 ) );
    f.beginPath();
    for ( let i = 0; i < 7; i++ ) {
      const a = -0.6 + i * 0.2;
      f.moveTo( X( -0.01 ), 0 );
      f.lineTo( X( -0.01 ) - Math.cos( a ) * X( 0.17 ), Math.sin( a ) * X( 0.17 ) );
    }
    f.stroke();
    f.restore();
    // the soft sail of the dorsal fin and the small anal fin beneath, their bases set into the body
    // (drawn first, so the body covers the roots and they sit on it, never floating: owner), rayed
    const rayLine = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.35 ), 0.55 );
    const softFin = ( base0: [ number, number ], base1: [ number, number ], tip: [ number, number ], belly: [ number, number ] ) => {
      f.fillStyle = fin;
      f.beginPath();
      f.moveTo( X( base0[ 0 ] ), X( base0[ 1 ] ) );
      f.quadraticCurveTo( X( tip[ 0 ] ), X( tip[ 1 ] ), X( belly[ 0 ] ), X( belly[ 1 ] ) );
      f.quadraticCurveTo( X( ( belly[ 0 ] + base1[ 0 ] ) / 2 ), X( ( belly[ 1 ] + base1[ 1 ] ) / 2 ), X( base1[ 0 ] ), X( base1[ 1 ] ) );
      f.closePath();
      f.fill();
      f.strokeStyle = rayLine;
      f.lineWidth = Math.max( 0.4, X( 0.003 ) );
      f.beginPath();
      for ( let i = 1; i <= 4; i++ ) {
        const t = i / 5;
        const bx = base0[ 0 ] + ( base1[ 0 ] - base0[ 0 ] ) * t, by = base0[ 1 ] + ( base1[ 1 ] - base0[ 1 ] ) * t;
        const ex = tip[ 0 ] + ( belly[ 0 ] - tip[ 0 ] ) * t * 0.9, ey = tip[ 1 ] + ( belly[ 1 ] - tip[ 1 ] ) * t * 0.9;
        f.moveTo( X( bx ), X( by ) );
        f.lineTo( X( ex ), X( ey ) );
      }
      f.stroke();
    };
    softFin( [ -0.04, -0.22 ], [ -0.27, -0.05 ], [ -0.16, -0.36 ], [ -0.32, -0.12 ] );
    softFin( [ -0.1, 0.17 ], [ -0.29, 0.04 ], [ -0.24, 0.27 ], [ -0.33, 0.1 ] );
    // the mouth's lips: the upper from the snout back to the corner, the lower a little ahead of it
    const upper = ( t: number ): [ number, number ] => [ X( 0.39 - 0.23 * t ), X( -0.02 + 0.06 * t - Math.sin( t * Math.PI ) * 0.012 ) ];
    const lower = ( t: number ): [ number, number ] => [ X( 0.42 - 0.26 * t ), X( 0.085 - 0.045 * t + Math.sin( t * Math.PI ) * 0.012 ) ];
    // the body, one smooth outline, the mouth a notch in it
    const body = f.createRadialGradient( X( 0.32 ), X( -0.16 ), 0, X( 0.05 ), 0, X( 0.45 ) );
    body.addColorStop( 0, this.rgba( this.mix( back, glowC, 0.14 * pulse ), 1 ) );
    body.addColorStop( 0.5, this.rgba( back, 1 ) );
    body.addColorStop( 1, this.rgba( this.mix( back, [ 0, 0, 0 ], 0.25 ), 1 ) );
    f.fillStyle = body;
    f.beginPath();
    let [ x, y ] = upper( 0 );
    f.moveTo( x, y );
    f.bezierCurveTo( X( 0.42 ), X( -0.12 ), X( 0.38 ), X( -0.22 ), X( 0.28 ), X( -0.27 ) );
    f.bezierCurveTo( X( 0.16 ), X( -0.32 ), X( -0.04 ), X( -0.28 ), X( -0.17 ), X( -0.16 ) );
    f.bezierCurveTo( X( -0.25 ), X( -0.09 ), X( -0.31 ), X( -0.05 ), X( -0.38 ), X( -0.04 ) );
    f.lineTo( X( -0.38 ), X( 0.04 ) );
    f.bezierCurveTo( X( -0.3 ), X( 0.06 ), X( -0.2 ), X( 0.2 ), X( 0.02 ), X( 0.25 ) );
    f.bezierCurveTo( X( 0.2 ), X( 0.28 ), X( 0.39 ), X( 0.22 ), X( 0.42 ), X( 0.085 ) );
    for ( let i = 1; i <= 12; i++ ) { [ x, y ] = lower( i / 12 ); f.lineTo( x, y ); }
    for ( let i = 12; i >= 0; i-- ) { [ x, y ] = upper( i / 12 ); f.lineTo( x, y ); }
    f.closePath();
    f.fill();
    // the fangs: a neat row of slender curved needles from both lips, just paler than the body
    f.fillStyle = this.rgba( this.mix( back, [ 215, 225, 222 ], 0.14 ), 1 );
    const fang = ( [ bx, by ]: [ number, number ], dir: number, len: number ) => {
      const w = X( 0.007 );
      f.beginPath();
      f.moveTo( bx - w, by );
      f.quadraticCurveTo( bx - w * 0.2, by + dir * len * 0.7, bx - w * 1.6, by + dir * len );
      f.quadraticCurveTo( bx + w * 0.3, by + dir * len * 0.6, bx + w, by );
      f.closePath();
      f.fill();
    };
    for ( let i = 0; i < 6; i++ ) {
      const t = 0.08 + i * 0.15;
      const len = X( ( i % 2 ? 0.035 : 0.055 ) * ( 1 - t * 0.5 ) );
      fang( upper( t ), 1, len );
      if ( i < 5 ) { fang( lower( t + 0.07 ), -1, len * 0.9 ); }
    }
    // the gill cover's curve behind the mouth, and a faint line along the flank to the tail
    f.strokeStyle = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.35 ), 0.5 );
    f.lineWidth = Math.max( 0.5, X( 0.004 ) );
    f.beginPath();
    f.moveTo( X( 0.12 ), X( -0.15 ) );
    f.quadraticCurveTo( X( 0.04 ), X( 0.0 ), X( 0.1 ), X( 0.17 ) );
    f.stroke();
    f.strokeStyle = this.rgba( this.mix( back, [ 120, 140, 140 ], 0.06 ), 0.4 );
    f.lineWidth = Math.max( 0.4, X( 0.003 ) );
    f.beginPath();
    f.moveTo( X( 0.06 ), X( -0.06 ) );
    f.bezierCurveTo( X( -0.08 ), X( -0.08 ), X( -0.22 ), X( -0.04 ), X( -0.37 ), X( -0.005 ) );
    f.stroke();
    // the small side fin, a leaf, rowing
    f.save();
    f.translate( X( 0.04 ), X( 0.1 ) );
    f.rotate( 0.55 + Math.sin( phase * 2.4 ) * 0.3 );
    f.fillStyle = fin;
    f.beginPath();
    f.moveTo( 0, 0 );
    f.quadraticCurveTo( X( -0.06 ), X( -0.05 ), X( -0.12 ), X( -0.01 ) );
    f.quadraticCurveTo( X( -0.06 ), X( 0.04 ), 0, 0 );
    f.fill();
    f.restore();
    // the rod: a tapering arc from the brow, over and forward, to the lure
    const bob = Math.sin( phase * 1.6 ) * X( 0.025 );
    // closer in, and the rod clearly joined to the brow at one end and to the lure at the other (owner)
    const lx = X( 0.5 ) + Math.sin( phase * 1.2 ) * X( 0.015 ), ly = X( -0.14 ) + bob;
    const rodTone = this.mix( back, [ 120, 140, 140 ], 0.08 );
    f.fillStyle = this.rgba( rodTone, 1 );
    f.beginPath();
    f.moveTo( X( 0.235 ), X( -0.27 ) );
    f.bezierCurveTo( X( 0.27 ), X( -0.48 ), X( 0.46 ), X( -0.46 ), lx - X( 0.004 ), ly - X( 0.018 ) );
    f.lineTo( lx + X( 0.004 ), ly - X( 0.018 ) );
    f.bezierCurveTo( X( 0.47 ), X( -0.42 ), X( 0.31 ), X( -0.44 ), X( 0.29 ), X( -0.28 ) );
    f.closePath();
    f.fill();
    // the lure: a soft halo and a bright orb
    f.save();
    f.globalCompositeOperation = 'lighter';
    const R = X( 0.28 ) * ( 1 + 0.4 * e );
    const halo = f.createRadialGradient( lx, ly, 0, lx, ly, R );
    halo.addColorStop( 0, this.rgba( glowC, 0.6 * pulse ) );
    halo.addColorStop( 0.22, this.rgba( glowC, 0.2 * pulse ) );
    halo.addColorStop( 1, this.rgba( glowC, 0 ) );
    f.fillStyle = halo;
    f.fillRect( lx - R, ly - R, R * 2, R * 2 );
    f.restore();
    const orb = f.createRadialGradient( lx - X( 0.006 ), ly - X( 0.006 ), 0, lx, ly, X( 0.024 ) );
    orb.addColorStop( 0, this.rgba( this.mix( glowC, [ 255, 255, 255 ], 0.65 ), 1 ) );
    orb.addColorStop( 1, this.rgba( lure, 1 ) );
    f.fillStyle = orb;
    f.beginPath();
    f.arc( lx, ly, X( 0.022 ), 0, Math.PI * 2 );
    f.fill();
    f.strokeStyle = this.rgba( glowC, 0.55 );
    f.lineWidth = Math.max( 0.4, X( 0.003 ) );
    for ( const side of [ -1, 1 ] ) {
      f.beginPath();
      f.moveTo( lx + side * X( 0.006 ), ly + X( 0.018 ) );
      f.quadraticCurveTo( lx + side * X( 0.02 ), ly + X( 0.05 ) + Math.sin( phase * 2 + side ) * X( 0.01 ), lx + side * X( 0.01 ), ly + X( 0.075 ) );
      f.stroke();
    }
  }

  /**
   * A great white shark in side view, drawn from its anatomy (owner): the conical snout, the black eye,
   * the underslung mouth, five gill slits, the tall triangular first dorsal with its trailing edge
   * curved in, the long sickle pectoral, the small pelvic, second dorsal and anal fins, the keel on the
   * narrow tail stock and the crescent tail, its two lobes nearly equal; dark like the other giants,
   * its paler belly meeting its back along the flank. It swims as sharks do, by sweeping its tail from
   * side to side: seen from the side the tail turns toward and away from us, narrowing and widening
   * and leaning, the rear of the body swaying a touch with it.
   */
  private shark( f: CanvasRenderingContext2D, L: number, phase: number, back: RGB, belly: RGB, water: RGB ): void {
    const TOP = [ [ 0, 0.004 ], [ 0.05, 0.028 ], [ 0.12, 0.05 ], [ 0.25, 0.075 ], [ 0.35, 0.082 ], [ 0.5, 0.075 ],
      [ 0.65, 0.055 ], [ 0.8, 0.032 ], [ 0.92, 0.018 ], [ 1, 0.014 ] ];
    const BOTTOM = [ [ 0, 0.004 ], [ 0.04, 0.018 ], [ 0.1, 0.04 ], [ 0.2, 0.06 ], [ 0.32, 0.07 ], [ 0.45, 0.066 ],
      [ 0.6, 0.05 ], [ 0.75, 0.032 ], [ 0.9, 0.018 ], [ 1, 0.014 ] ];
    const curve = ( T: number[][], u: number ) => {
      let i = 1;
      while ( i < T.length - 1 && u > T[ i ][ 0 ] ) { i++; }
      const p0 = T[ Math.max( 0, i - 2 ) ], p1 = T[ i - 1 ], p2 = T[ i ], p3 = T[ Math.min( T.length - 1, i + 1 ) ];
      const t = Math.max( 0, Math.min( 1, ( u - p1[ 0 ] ) / ( p2[ 0 ] - p1[ 0 ] || 1 ) ) );
      return 0.5 * ( 2 * p1[ 1 ] + ( -p0[ 1 ] + p2[ 1 ] ) * t + ( 2 * p0[ 1 ] - 5 * p1[ 1 ] + 4 * p2[ 1 ] - p3[ 1 ] ) * t * t
        + ( -p0[ 1 ] + 3 * p1[ 1 ] - 3 * p2[ 1 ] + p3[ 1 ] ) * t * t * t ) * L;
    };
    const B = 0.82 * L;
    const sweep = Math.sin( phase * 2 );
    const toward = Math.cos( phase * 2 );
    // the sway: the rear only, a touch, as the tail swings
    const bend = ( u: number ) => 0.01 * L * Math.pow( Math.max( 0, ( u - 0.5 ) / 0.5 ), 2 ) * sweep;
    const top = ( u: number ) => curve( TOP, u );
    const bottom = ( u: number ) => curve( BOTTOM, u );
    const body = this.spineBody( B, bend, top, bottom );
    const dark = this.mix( back, [ 0, 0, 0 ], 0.38 );
    const fill = this.rgba( back, 1 );
    const tri = ( pts: [ number, number ][] ) => {
      f.beginPath();
      f.moveTo( pts[ 0 ][ 0 ], pts[ 0 ][ 1 ] );
      for ( let i = 1; i < pts.length; i += 2 ) { f.quadraticCurveTo( pts[ i ][ 0 ], pts[ i ][ 1 ], pts[ i + 1 ][ 0 ], pts[ i + 1 ][ 1 ] ); }
      f.closePath();
      f.fill();
    };
    // the crescent tail, turning with the sweep: narrower and leaning as it swings toward or away
    const root = body.at( 1 );
    f.save();
    f.translate( root.x, root.y );
    f.transform( 0.45 + 0.55 * Math.abs( toward ), 0, sweep * 0.12, 1, 0, 0 );
    f.fillStyle = this.rgba( this.mix( back, [ 0, 0, 0 ], 0.08 * ( 1 - Math.abs( toward ) ) ), 1 );
    tri( [ [ 0.02 * L, -0.012 * L ],
      [ -0.05 * L, -0.07 * L ], [ -0.15 * L, -0.17 * L ],
      [ -0.1 * L, -0.07 * L ], [ -0.07 * L, 0 ],
      [ -0.1 * L, 0.065 * L ], [ -0.135 * L, 0.14 * L ],
      [ -0.04 * L, 0.06 * L ], [ 0.02 * L, 0.012 * L ] ] );
    f.restore();
    // the body: dark above, the paler belly meeting it along the flank
    const shading = f.createLinearGradient( 0, -0.08 * L, 0, 0.07 * L );
    shading.addColorStop( 0, this.rgba( back, 1 ) );
    shading.addColorStop( 0.55, this.rgba( back, 1 ) );
    shading.addColorStop( 0.66, this.rgba( belly, 1 ) );
    shading.addColorStop( 1, this.rgba( belly, 1 ) );
    f.fillStyle = shading;
    this.smoothPath( f, body.outline );
    f.fill();
    f.fillStyle = fill;
    // the first dorsal: tall, its leading edge straight up and back, its trailing edge curved in
    const d0 = body.at( 0.33 ), d1 = body.at( 0.47 );
    const dy0 = d0.y - top( 0.33 ) + 0.004 * L, dy1 = d1.y - top( 0.47 ) + 0.004 * L;
    tri( [ [ d0.x, dy0 ], [ d0.x - 0.03 * L, dy0 - 0.09 * L ], [ d0.x - 0.07 * L, dy0 - 0.15 * L ],
      [ d0.x - 0.075 * L, dy0 - 0.06 * L ], [ d1.x, dy1 ] ] );
    // the long sickle pectoral, angled down and back, its tip curving
    const pc = body.at( 0.24 );
    const pcy = pc.y + bottom( 0.24 ) * 0.55;
    tri( [ [ pc.x + 0.01 * L, pcy - 0.01 * L ], [ pc.x - 0.05 * L, pcy + 0.05 * L ], [ pc.x - 0.11 * L, pcy + 0.13 * L + toward * 0.004 * L ],
      [ pc.x - 0.07 * L, pcy + 0.05 * L ], [ pc.x - 0.07 * L, pcy + 0.004 * L ] ] );
    // the small fins behind: pelvic and anal beneath, the second dorsal above
    for ( const [ u, up, h ] of [ [ 0.63, 1, 0.03 ], [ 0.8, 1, 0.02 ], [ 0.79, -1, 0.022 ] ] ) {
      const p = body.at( u );
      const y = up > 0 ? p.y + bottom( u ) - 0.002 * L : p.y - top( u ) + 0.002 * L;
      tri( [ [ p.x + 0.012 * L, y ], [ p.x - 0.01 * L, y + up * h * L * 0.5 ], [ p.x - 0.022 * L, y + up * h * L ],
        [ p.x - 0.02 * L, y + up * h * L * 0.3 ], [ p.x - 0.03 * L, y ] ] );
    }
    // the keel along the tail stock
    f.strokeStyle = this.rgba( dark, 0.6 );
    f.lineWidth = Math.max( 0.6, L * 0.004 );
    f.beginPath();
    const k0 = body.at( 0.9 ), k1 = body.at( 1 );
    f.moveTo( k0.x, k0.y );
    f.lineTo( k1.x, k1.y );
    f.stroke();
    // the five gill slits, curving
    f.strokeStyle = this.rgba( dark, 0.85 );
    f.lineWidth = Math.max( 0.6, L * 0.0045 );
    for ( let i = 0; i < 5; i++ ) {
      const u = 0.19 + i * 0.022;
      const p = body.at( u );
      const h = ( top( u ) + bottom( u ) ) * ( 0.4 - i * 0.02 );
      f.beginPath();
      f.moveTo( p.x + 0.004 * L, p.y - h * 0.45 );
      f.quadraticCurveTo( p.x - 0.006 * L, p.y, p.x + 0.002 * L, p.y + h * 0.55 );
      f.stroke();
    }
    // the mouth, underslung, its corner turned down
    f.strokeStyle = this.rgba( this.mix( dark, water, 0.15 ), 0.8 );
    f.lineWidth = Math.max( 0.6, L * 0.004 );
    f.beginPath();
    const m0 = body.at( 0.05 ), m1 = body.at( 0.15 );
    f.moveTo( m0.x, m0.y + bottom( 0.05 ) * 0.7 );
    f.quadraticCurveTo( ( m0.x + m1.x ) / 2, m0.y + bottom( 0.1 ) * 0.95, m1.x, m1.y + bottom( 0.15 ) * 0.6 );
    f.stroke();
    // the black eye
    const eye = body.at( 0.1 );
    f.fillStyle = this.rgba( [ 0, 0, 0 ], 1 );
    f.beginPath();
    f.arc( eye.x, eye.y - top( 0.1 ) * 0.25, L * 0.008, 0, Math.PI * 2 );
    f.fill();
  }

  /** The light from the surface: soft slanting rays, each swaying and breathing on the loop. */
  private drawRays( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const t = this.time * W0;
    const [ , green, cyan ] = this.five();
    const tone = this.mix( this.mix( cyan, green, 0.3 ), [ 255, 255, 255 ], 0.25 );
    // painted at a quarter size and drawn up, so their edges come out soft, never ruled lines
    const rs = 0.25;
    const scale = ctx.getTransform().a;
    if ( !this.rayCanvas ) { this.rayCanvas = document.createElement( 'canvas' ); }
    const rc = this.rayCanvas;
    const rw = Math.ceil( W * scale * rs ), rh = Math.ceil( H * scale * rs );
    if ( rc.width !== rw || rc.height !== rh ) { rc.width = rw; rc.height = rh; }
    const r = rc.getContext( '2d' )!;
    r.setTransform( scale * rs, 0, 0, scale * rs, 0, 0 );
    r.clearRect( 0, 0, W, H );
    const UW = Math.min( W, H * 2.1 );
    // half as many and weaker (owner)
    for ( let i = 0; i < 9; i += 2 ) {
      const x0 = W / 2 + ( i - 4 ) * UW * 0.13 + Math.sin( t * ( 1 + ( i % 3 ) ) + i ) * UW * 0.02;
      const width = UW * ( 0.03 + 0.02 * ( ( i * 7 ) % 3 ) );
      const len = H * ( 0.6 + 0.15 * ( ( i * 5 ) % 3 ) );
      const a = 0.03 + 0.025 * ( 0.5 + 0.5 * Math.sin( t * ( 2 + ( i % 2 ) ) + i * 1.7 ) );
      const g = r.createLinearGradient( 0, 0, 0, len );
      g.addColorStop( 0, this.rgba( tone, a ) );
      g.addColorStop( 1, this.rgba( tone, 0 ) );
      r.fillStyle = g;
      r.beginPath();
      r.moveTo( x0 - width / 2, 0 );
      r.lineTo( x0 + width / 2, 0 );
      r.lineTo( x0 + width * 1.8 + len * 0.18, len );
      r.lineTo( x0 - width * 0.4 + len * 0.18, len );
      r.closePath();
      r.fill();
    }
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage( rc, 0, 0, Math.floor( W * scale * rs ), Math.floor( H * scale * rs ), 0, 0, W, H );
    // the surface itself, right at the top, seen from below (owner: higher, and real): a bright
    // rippling underside, its edge never straight, crossed by a net of light where the waves focus it
    // the waves running along it, visibly (owner), every one a whole number of turns in the loop
    const kk = this.scaleK;
    const surf = ( x: number ) => H * 0.014 + ( Math.sin( x * 0.016 + t * 9 ) * 4 + Math.sin( x * 0.041 - t * 14 ) * 2.4 + Math.sin( x * 0.09 + t * 23 ) * 1.1 ) * kk;
    const sheen = ctx.createLinearGradient( 0, 0, 0, H * 0.03 );
    // a little darker (owner)
    sheen.addColorStop( 0, this.rgba( tone, 0.18 ) );
    sheen.addColorStop( 1, this.rgba( tone, 0.02 ) );
    ctx.fillStyle = sheen;
    ctx.beginPath();
    ctx.moveTo( 0, 0 );
    for ( let x = 0; x <= W; x += 6 ) { ctx.lineTo( x, surf( x ) ); }
    ctx.lineTo( W, 0 );
    ctx.closePath();
    ctx.fill();
    // the net of light just under it: little bright cells, each breathing
    for ( let i = 0; i < 70; i++ ) {
      const x = ( i / 70 ) * W + Math.sin( t * ( 6 + ( i % 5 ) ) + i ) * 14;
      const y = surf( x ) + 2 + ( i % 3 ) * 2;
      const a = 0.05 + 0.06 * Math.max( 0, Math.sin( t * ( 2 + ( i % 4 ) ) + i * 1.3 ) );
      ctx.save();
      ctx.translate( x, y );
      ctx.scale( ( 10 + ( i * 37 ) % 14 ) * this.scaleK, 2.2 * this.scaleK );
      const cg = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cg.addColorStop( 0, this.rgba( this.mix( tone, [ 255, 255, 255 ], 0.4 ), a ) );
      cg.addColorStop( 1, this.rgba( tone, 0 ) );
      ctx.fillStyle = cg;
      ctx.fillRect( -1, -1, 2, 2 );
      ctx.restore();
    }
    ctx.restore();
  }

  /** Marine snow drifting down, and the plankton glowing in its own rhythm, always the same amount. */
  private drawMotes( ctx: CanvasRenderingContext2D, dt: number, front: boolean ): void {
    const W = this.width;
    const H = this.height;
    const t = this.time * W0;
    const [ , green, cyan ] = this.five();
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    this.motes.forEach( ( m, i ) => {
      if ( ( i % 3 === 0 ) !== front ) { return; }
      if ( dt > 0 ) {
        m.y += m.sink * dt;
        m.x += m.drift * dt;
        if ( m.y > H + 4 ) { m.y = -4; }
        if ( m.x > W + 4 ) { m.x = -4; } else if ( m.x < -4 ) { m.x = W + 4; }
      }
      const x = m.x + Math.sin( t * m.rate + m.phase ) * 6 * this.scaleK;
      if ( m.glow ) {
        // steady: the counter brings out fish, not lights (owner)
        if ( m.rank > 0.35 ) { return; }
        const pulse = 0.4 + 0.6 * Math.pow( 0.5 + 0.5 * Math.sin( t * m.rate * 2 + m.phase ), 3 );
        // only down in the deep, fading out above the middle (owner)
        const deep = Math.max( 0, Math.min( 1, ( m.y - H * 0.45 ) / ( H * 0.2 ) ) );
        if ( deep <= 0 ) { return; }
        // each speck in one of the circle's second to fifth colours, more saturated (owner)
        const base = this.five()[ 1 + ( i % 4 ) ];
        const grey = ( base[ 0 ] + base[ 1 ] + base[ 2 ] ) / 3;
        const tone = base.map( c => Math.max( 0, Math.min( 255, grey + ( c - grey ) * 1.45 ) ) ) as RGB;
        const r = m.r * 2.6;
        const g = ctx.createRadialGradient( x, m.y, 0, x, m.y, r );
        g.addColorStop( 0, this.rgba( tone, 0.42 * pulse * deep ) );
        g.addColorStop( 1, this.rgba( tone, 0 ) );
        ctx.fillStyle = g;
        ctx.fillRect( x - r, m.y - r, r * 2, r * 2 );
      } else {
        ctx.fillStyle = this.rgba( this.mix( cyan, [ 255, 255, 255 ], 0.5 ), 0.12 );
        ctx.beginPath();
        ctx.arc( x, m.y, m.r, 0, Math.PI * 2 );
        ctx.fill();
      }
    } );
    ctx.restore();
  }

  /**
   * The schools: each a loose teardrop of little dark fish rippling as it swims across, as Dock Town
   * draws them; a couple at rest, more and faster in a gust. They come out of the dark and go back
   * into it slowly, growing smaller as they fade.
   */
  private drawSchools( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const e = this.energy;
    // a few at rest, many more while the counter counts (owner)
    const share = 0.1 + 0.9 * e;
    const [ teal, , cyan ] = this.five();
    const glint = this.mix( cyan, [ 255, 255, 255 ], 0.4 );
    for ( const s of this.schools ) {
      // paler near the surface, darker down deep (owner), steeped in the water at their depth
      const depth = Math.max( 0, Math.min( 1, s.y / this.height ) );
      const body = this.mix( this.waterAt( s.y ), [ 4, 10, 12 ], 0.25 + 0.55 * depth );
      const want = s.rank <= share ? 1 : 0;
      if ( dt <= 0 ) {
        s.shown = want;
      } else {
        // one called out comes up out of the dark somewhere new, smoothly (owner); one sent away
        // fades off slowly
        if ( want && s.shown < 0.02 ) {
          s.x = Math.random() * W;
          // anywhere from high up to just over the bed (owner)
          s.y = this.height * ( 0.06 + Math.random() * 0.76 );
        }
        s.shown += ( want - s.shown ) * Math.min( 1, dt * ( want > s.shown ? 0.9 : 0.45 ) );
      }
      if ( dt > 0 ) {
        // much quicker while the counter counts (owner)
        s.x += s.dir * s.speed * ( 1 + 1.6 * e ) * dt;
        s.phase += dt * 1.6;
        const m = s.size * 1.5;
        if ( s.x > W + m ) { s.x = -m; } else if ( s.x < -m ) { s.x = W + m; }
      }
      if ( s.shown < 0.02 ) { continue; }
      const away = 1 - s.shown;
      const sz = s.size * ( 1 - 0.4 * away );
      const y = s.y + Math.sin( s.phase * 0.4 ) * sz * 0.5;
      ctx.save();
      ctx.globalAlpha = Math.pow( s.shown, 0.7 ) * 0.9;
      ctx.translate( s.x, y );
      ctx.scale( s.dir, 1 );
      this.drawSchool( ctx, s, sz, body, glint );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /** One school, at the origin facing right, `sz` across: its fish rippling as it swims. */
  private drawSchool( ctx: CanvasRenderingContext2D, s: School, sz: number, body: RGB, glint: RGB ): void {
      s.fish.forEach( ( f, i ) => {
        const spread = ( 0.1 + 0.32 * Math.sin( f.t * Math.PI ) ) * sz;
        const wiggle = Math.sin( s.phase * 3 + i * 0.9 ) * 0.035 * sz;
        const x = ( 0.78 - 1.5 * f.t ) * sz + wiggle;
        const fy = f.across * spread + wiggle * 0.4;
        const len = ( 0.1 + f.size * 0.05 ) * sz;
        ctx.fillStyle = this.rgba( body, 0.9 );
        ctx.beginPath();
        ctx.ellipse( x, fy, len, len * 0.36, 0, 0, Math.PI * 2 );
        ctx.fill();
        // the tail, flicking
        const flick = Math.sin( s.phase * 8 + i ) * len * 0.15;
        ctx.beginPath();
        ctx.moveTo( x - len * 0.8, fy );
        ctx.lineTo( x - len * 1.45, fy - len * 0.4 + flick );
        ctx.lineTo( x - len * 1.45, fy + len * 0.4 + flick );
        ctx.closePath();
        ctx.fill();
        if ( i % 4 === 0 ) {
          ctx.fillStyle = this.rgba( glint, 0.35 );
          ctx.beginPath();
          ctx.ellipse( x + len * 0.15, fy - len * 0.12, len * 0.45, len * 0.1, 0, 0, Math.PI * 2 );
          ctx.fill();
        }
      } );
  }

  /** A few bubbles rising from the bed, wobbling, catching the light. */
  private drawBubbles( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    if ( dt > 0 ) {
      this.bubbleDue += dt * 1.5;
      while ( this.bubbleDue >= 1 ) {
        this.bubbleDue -= 1;
        this.bubbles.push( { x: Math.random() * W, y: H * 0.95, r: ( 1.5 + Math.random() * 3 ) * k, speed: ( 30 + Math.random() * 40 ) * k, wobble: Math.random() * 6 } );
      }
      for ( const b of this.bubbles ) { b.y -= b.speed * dt; b.wobble += dt * 3; }
      this.bubbles = this.bubbles.filter( b => b.y > -10 );
    }
    const [ , , cyan ] = this.five();
    const rim = this.mix( cyan, [ 255, 255, 255 ], 0.5 );
    for ( const b of this.bubbles ) {
      const x = b.x + Math.sin( b.wobble ) * 3 * k;
      // fading as they rise toward the surface's glare
      const a = Math.min( 1, ( b.y / H ) * 1.6 );
      ctx.strokeStyle = this.rgba( rim, 0.35 * a );
      ctx.lineWidth = Math.max( 0.6, 0.8 * k );
      ctx.beginPath();
      ctx.arc( x, b.y, b.r, 0, Math.PI * 2 );
      ctx.stroke();
      ctx.fillStyle = this.rgba( rim, 0.25 * a );
      ctx.beginPath();
      ctx.arc( x - b.r * 0.35, b.y - b.r * 0.35, b.r * 0.3, 0, Math.PI * 2 );
      ctx.fill();
    }
  }

  /** The seagrass, behind the rocks or in clumps in front of them, each blade a tapered soft leaf swaying on the loop. */
  private drawGrass( ctx: CanvasRenderingContext2D, front: boolean, clump = -1 ): void {
    const H = this.height;
    const t = this.time * W0;
    const [ teal, green ] = this.five();
    const dark = this.mix( this.mix( green, teal, 0.6 ), [ 4, 10, 12 ], 0.55 );
    const lit = this.mix( this.mix( green, teal, 0.4 ), [ 4, 10, 12 ], 0.35 );
    for ( const b of this.grass ) {
      if ( b.front !== front || ( front && b.clump !== clump ) ) { continue; }
      const base = b.base;
      const sway = Math.sin( t * b.rate + b.phase ) * b.h * 0.18 + b.h * 0.06 * Math.sin( t * 2 + b.x * 0.01 );
      ctx.fillStyle = this.rgba( this.mix( b.front ? this.mix( dark, [ 4, 10, 12 ], 0.35 ) : dark, lit, b.tone ), b.front ? 0.95 : 0.85 );
      ctx.beginPath();
      ctx.moveTo( b.x - b.w / 2, base );
      ctx.quadraticCurveTo( b.x - b.w * 0.4 + sway * 0.3, base - b.h * 0.55, b.x + sway, base - b.h );
      ctx.quadraticCurveTo( b.x + b.w * 0.4 + sway * 0.3, base - b.h * 0.55, b.x + b.w / 2, base );
      ctx.closePath();
      ctx.fill();
    }
    if ( !front ) { return; }
    // the sand drifted over the roots of the clumps in front, an uneven little heap, never a line
    const [ , , , , sand ] = this.five();
    // a tone darker than the bed (owner)
    const ground = this.mix( this.mix( this.mix( teal, [ 4, 10, 12 ], 0.7 ), sand, 0.08 ), [ 4, 10, 12 ], 0.3 );
    for ( const c of [ this.clumps[ clump ] ] ) {
      for ( let j = 0; j < 3; j++ ) {
        const x = c.x + ( j - 1 ) * c.w * 0.3;
        const y = c.y + 2 + j % 2 * 3;
        ctx.save();
        ctx.translate( x, y );
        ctx.scale( c.w * ( 0.5 + 0.15 * j ), c.w * 0.16 );
        const g = ctx.createRadialGradient( 0, 0.2, 0, 0, 0.3, 1 );
        g.addColorStop( 0, this.rgba( ground, 1 ) );
        g.addColorStop( 0.6, this.rgba( ground, 0.85 ) );
        g.addColorStop( 1, this.rgba( ground, 0 ) );
        ctx.fillStyle = g;
        ctx.fillRect( -1, -1, 2, 2 );
        ctx.restore();
      }
    }
  }

  /** The wallpaper's schools: a few, scattered, each caught in its swim. */
  private drawWallSchools( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( 8 );
    const [ teal, , cyan ] = this.five();
    // dark, as on the board (owner)
    const body = this.mix( teal, [ 6, 14, 18 ], 0.6 );
    const glint = this.mix( cyan, [ 255, 255, 255 ], 0.4 );
    const n = Math.max( 6, Math.round( W * H / 80000 ) );
    for ( let i = 0; i < n; i++ ) {
      const s = this.schools[ i % this.schools.length ];
      if ( !s ) { break; }
      ctx.save();
      ctx.globalAlpha = 0.75 + rand() * 0.25;
      ctx.translate( rand() * W, rand() * H );
      ctx.scale( rand() < 0.5 ? 1 : -1, 1 );
      this.drawSchool( ctx, s, ( 30 + rand() * 20 ) * k, body, glint );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still parts, painted once: the water deepening from the teal glow under the surface to near
   * black, and, on a layer of its own drawn over the grass, the seabed in shadow with its rocks and
   * corals, soft (painted small and drawn up).
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = Math.min( W, H * 2.1 );
    const [ teal, green, cyan, coral, sand ] = this.five();
    const night: RGB = [ 4, 10, 12 ];
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const water = g.createLinearGradient( 0, 0, 0, H );
    // the sea a tone toward the theme's green (owner)
    // and a tone darker (owner)
    const sea = this.mix( this.mix( teal, green, 0.16 ), night, 0.18 );
    water.addColorStop( 0, this.rgba( this.mix( this.mix( sea, cyan, 0.4 ), night, 0.15 ), 1 ) );
    water.addColorStop( 0.35, this.rgba( this.mix( sea, night, 0.25 ), 1 ) );
    water.addColorStop( 0.75, this.rgba( this.mix( sea, night, 0.6 ), 1 ) );
    water.addColorStop( 1, this.rgba( this.mix( sea, night, 0.82 ), 1 ) );
    g.fillStyle = water;
    g.fillRect( 0, 0, W, H );
    // a wide soft glow under the surface, where the sun is
    const R = Math.max( W, H );
    const sg = g.createRadialGradient( W / 2 + UW * 0.1, -H * 0.1, 0, W / 2 + UW * 0.1, -H * 0.1, R * 0.7 );
    sg.addColorStop( 0, this.rgba( this.mix( cyan, [ 255, 255, 255 ], 0.2 ), 0.35 ) );
    sg.addColorStop( 1, this.rgba( cyan, 0 ) );
    g.fillStyle = sg;
    g.fillRect( 0, 0, W, H );
    // a clearing behind the hero's words, a little darker, so they read
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( this.mix( teal, night, 0.5 ), 0.3 ) );
      cl.addColorStop( 1, this.rgba( teal, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;

    // the seabed: rocks and corals in shadow, painted at half size so they come out soft
    const bs = 0.5;
    const bc = document.createElement( 'canvas' );
    bc.width = Math.ceil( W * scale );
    bc.height = Math.ceil( H * scale );
    const b2 = bc.getContext( '2d' )!;
    b2.scale( scale, scale );
    const lo = document.createElement( 'canvas' );
    lo.width = Math.ceil( W * scale * bs );
    lo.height = Math.ceil( H * scale * bs );
    const l = lo.getContext( '2d' )!;
    l.scale( scale * bs, scale * bs );
    const rand = this.seededLocal( 17 );
    const rock = this.mix( teal, night, 0.7 );
    // the floor itself, rolling, repeating to the edges
    const floorY = ( x: number ) => this.floorY( x );
    const fl = l.createLinearGradient( 0, H * 0.86, 0, H );
    fl.addColorStop( 0, this.rgba( this.mix( rock, sand, 0.08 ), 1 ) );
    fl.addColorStop( 1, this.rgba( this.mix( rock, night, 0.5 ), 1 ) );
    l.fillStyle = fl;
    l.beginPath();
    l.moveTo( -2, H );
    for ( let x = -2; x <= W + 2; x += 4 ) { l.lineTo( x, floorY( x ) ); }
    l.lineTo( W + 2, H );
    l.closePath();
    l.fill();
    // the sand's own texture: soft ripples of lighter sand and a scatter of pebbles
    for ( let i = 0; i < 60; i++ ) {
      const x = W / 2 + ( rand() - 0.5 ) * Math.max( W, UW ) * 1.05;
      const y = floorY( x ) + ( H - floorY( x ) ) * ( 0.1 + rand() * 0.8 );
      l.save();
      l.translate( x, y );
      l.scale( ( 20 + rand() * 50 ) * k, ( 1.5 + rand() * 2 ) * k );
      const rg = l.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      rg.addColorStop( 0, this.rgba( this.mix( rock, sand, 0.22 ), 0.35 ) );
      rg.addColorStop( 1, this.rgba( rock, 0 ) );
      l.fillStyle = rg;
      l.fillRect( -1, -1, 2, 2 );
      l.restore();
    }
    for ( let i = 0; i < 90; i++ ) {
      const x = rand() * W;
      const y = floorY( x ) + ( H - floorY( x ) ) * rand() * 0.9;
      const pr = ( 1 + rand() * 3 ) * k;
      l.fillStyle = this.rgba( this.mix( rock, night, 0.3 + rand() * 0.3 ), 0.8 );
      l.beginPath();
      l.ellipse( x, y, pr * 1.4, pr, 0, 0, Math.PI * 2 );
      l.fill();
    }
    // rocks and coral heads along it, each its own shape (owner: not flat-bottomed mounds): an uneven
    // rounded outline settled into the sand, lit from above and dark beneath, mottled with polyps and
    // sponges in faint coral and sand; some carry little tube sponges or a brain coral's ridges
    const span = Math.max( W, UW );
    const sponges: ( () => void )[] = [];
    for ( let i = 0; i < 34; i++ ) {
      const x = W / 2 + ( rand() - 0.5 ) * span * 1.05;
      const y = floorY( x ) + 6 * k;
      const r = ( 14 + rand() * 40 ) * k;
      const wide = 1 + rand() * 0.7;
      const tall = 0.55 + rand() * 0.45;
      const tint = rand() < 0.35 ? coral : rand() < 0.5 ? sand : green;
      const tone = this.mix( rock, tint, 0.08 + rand() * 0.1 );
      // the outline: a closed, uneven loop, its lower part sunk below the sand line
      const pts: [ number, number ][] = [];
      const n = 14;
      const bumps = [ rand(), rand(), rand(), rand() ];
      for ( let j = 0; j < n; j++ ) {
        const a = Math.PI + ( j / n ) * Math.PI * 2;
        const wob = 1 + 0.16 * Math.sin( a * 2 + bumps[ 0 ] * 6 ) + 0.1 * Math.sin( a * 3 + bumps[ 1 ] * 6 ) + 0.06 * ( bumps[ j % 4 ] - 0.5 );
        const below = Math.sin( a ) > 0;
        // its lower part sunk well into the bed, so none sits above an empty gap (owner)
        pts.push( [ x + Math.cos( a ) * r * wide * wob, y + Math.sin( a ) * r * ( below ? 0.6 : tall ) * wob ] );
      }
      const path = () => {
        l.beginPath();
        const m0 = [ ( pts[ 0 ][ 0 ] + pts[ n - 1 ][ 0 ] ) / 2, ( pts[ 0 ][ 1 ] + pts[ n - 1 ][ 1 ] ) / 2 ];
        l.moveTo( m0[ 0 ], m0[ 1 ] );
        for ( let j = 0; j < n; j++ ) {
          const p0 = pts[ j ], p1 = pts[ ( j + 1 ) % n ];
          l.quadraticCurveTo( p0[ 0 ], p0[ 1 ], ( p0[ 0 ] + p1[ 0 ] ) / 2, ( p0[ 1 ] + p1[ 1 ] ) / 2 );
        }
        l.closePath();
      };
      const top = y - r * tall;
      const mg = l.createRadialGradient( x - r * 0.2, top + r * 0.15, 0, x, y, r * wide * 1.1 );
      mg.addColorStop( 0, this.rgba( this.mix( tone, this.mix( tint, [ 255, 255, 255 ], 0.2 ), 0.14 ), 1 ) );
      mg.addColorStop( 0.6, this.rgba( tone, 1 ) );
      mg.addColorStop( 1, this.rgba( this.mix( tone, night, 0.4 ), 1 ) );
      path();
      l.fillStyle = mg;
      l.fill();
      l.save();
      path();
      l.clip();
      // dark beneath, where it meets the sand
      const ug = l.createLinearGradient( 0, y - r * tall * 0.3, 0, y + r * 0.6 );
      ug.addColorStop( 0, this.rgba( night, 0 ) );
      ug.addColorStop( 1, this.rgba( night, 0.55 ) );
      l.fillStyle = ug;
      l.fillRect( x - r * wide * 1.3, y - r, r * wide * 2.6, r * 1.8 );
      // polyps and sponges, mottling it
      for ( let j = 0; j < 14; j++ ) {
        const px = x + ( rand() - 0.5 ) * r * wide * 1.6;
        const py = top + rand() * r * tall * 1.1;
        const pr = ( 1.5 + rand() * 4 ) * k;
        const pc = rand() < 0.5 ? this.mix( tone, coral, 0.25 ) : rand() < 0.5 ? this.mix( tone, sand, 0.2 ) : this.mix( tone, night, 0.35 );
        const sg2 = l.createRadialGradient( px, py, 0, px, py, pr );
        sg2.addColorStop( 0, this.rgba( pc, 0.7 ) );
        sg2.addColorStop( 1, this.rgba( pc, 0 ) );
        l.fillStyle = sg2;
        l.fillRect( px - pr, py - pr, pr * 2, pr * 2 );
      }
      // a brain coral's soft winding ridges on some
      if ( rand() < 0.3 ) {
        l.strokeStyle = this.rgba( this.mix( tone, night, 0.35 ), 0.45 );
        l.lineWidth = 1.4 * k;
        for ( let j = 0; j < 5; j++ ) {
          const yy = top + r * tall * ( 0.2 + j * 0.16 );
          l.beginPath();
          for ( let xx = x - r * wide; xx <= x + r * wide; xx += 3 * k ) {
            const yyy = yy + Math.sin( xx * 0.25 / k + j * 2 ) * 2 * k;
            if ( xx === x - r * wide ) { l.moveTo( xx, yyy ); } else { l.lineTo( xx, yyy ); }
          }
          l.stroke();
        }
      }
      l.restore();
      // little tube sponges standing on a few
      if ( rand() < 0.25 ) {
        // decided now (the seed's order stays), drawn once every rock is down
        const sx = x, sr = r, sw = wide, stop = top, stall = tall;
        const tubes = 2 + Math.floor( rand() * 3 );
        const parts: number[][] = [];
        for ( let j = 0; j < tubes; j++ ) { parts.push( [ rand(), rand(), rand(), rand() ] ); }
        sponges.push( () => {
          for ( const [ r1, r2, r3, r4 ] of parts ) {
            const tx = sx + ( r1 - 0.5 ) * sr * sw * 0.8;
            const th = ( 10 + r2 * 18 ) * k;
            const tw = ( 3 + r3 * 3 ) * k;
            const ty = stop + sr * stall * 0.15;
            const tg = l.createLinearGradient( tx - tw, 0, tx + tw, 0 );
            const tc = this.mix( rock, coral, 0.2 + r4 * 0.15 );
            tg.addColorStop( 0, this.rgba( this.mix( tc, night, 0.35 ), 1 ) );
            tg.addColorStop( 0.4, this.rgba( tc, 1 ) );
            tg.addColorStop( 1, this.rgba( this.mix( tc, night, 0.45 ), 1 ) );
            l.fillStyle = tg;
            l.beginPath();
            l.moveTo( tx - tw, ty );
            l.lineTo( tx - tw * 0.8, ty - th );
            l.quadraticCurveTo( tx, ty - th - tw * 0.6, tx + tw * 0.8, ty - th );
            l.lineTo( tx + tw, ty );
            l.closePath();
            l.fill();
            l.fillStyle = this.rgba( this.mix( tc, night, 0.7 ), 1 );
            l.beginPath();
            l.ellipse( tx, ty - th, tw * 0.6, tw * 0.25, 0, 0, Math.PI * 2 );
            l.fill();
          }
        } );
      }
    }
    sponges.forEach( draw => draw() );
    b2.drawImage( lo, 0, 0, Math.floor( W * scale * bs ), Math.floor( H * scale * bs ), 0, 0, W, H );
    // the water's haze over the bed's top edge, so it sinks into the dark
    const hz = b2.createLinearGradient( 0, H * 0.8, 0, H * 0.95 );
    hz.addColorStop( 0, this.rgba( this.mix( teal, night, 0.6 ), 0 ) );
    hz.addColorStop( 1, this.rgba( this.mix( teal, night, 0.6 ), 0.35 ) );
    b2.globalCompositeOperation = 'source-atop';
    b2.fillStyle = hz;
    b2.fillRect( 0, H * 0.8, W, H * 0.2 );
    this.bed = bc;
  }

  /** The seabed's line at x: rolling, laid out by the height, repeating to the edges. */
  private floorY( x: number ): number {
    const W = this.width;
    const H = this.height;
    const UW = Math.min( W, H * 2.1 );
    const t = ( x - W / 2 ) / UW * Math.PI * 2;
    return H * ( 0.92 - 0.02 * Math.sin( t * 1.3 + 0.5 ) - 0.012 * Math.sin( t * 3.7 + 2 ) );
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Ocean's own: deep teal, sea green, cyan, coral, sand. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 28, 66, 71 ], [ 90, 181, 144 ], [ 63, 180, 216 ], [ 220, 119, 131 ], [ 234, 192, 137 ] ];
  }
}
